import Link from 'next/link';
import { useEffect, useState } from 'react';

import cn from '@/lib/clsxm';
import { priceFormatter } from '@/lib/helper';

import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import { showToast } from '@/components/@base/toast/show-toast';
import { Text } from '@/components/@base/typography';
import XImage from '@/components/@base/x-image';
import Auth from '@/components/app/auth/components';
import Section from '@/components/app/product/components/@shared/section';
import AffiliateRulesModal from '@/components/app/product/components/affiliate-card/affiliate-rules-modal';
import Props from '@/components/app/product/components/affiliate-card/type';
import { useGetProfile } from '@/components/layout/services/hooks';
import useCopyToClipboard from '@/components/shared/helpers/hooks/use-copy-to-clipboard';

import useCommonModalStore from '@/store/common-modal-store';

import {
  DATA_CY_AFFILIATE_CARD_GET_LINK,
  DATA_CY_AFFILIATE_CARD_MORE_INFO,
  DATA_CY_AFFILIATE_CARD_PRODUCT_TITLE,
  DATA_CY_AFFILIATE_CARD_START_AFFILIATE,
} from '@/constant/cypress';
import imagesList from '@/constant/images-list';
import { pageLevelLocalization } from '@/constant/localization';

const { product: productLocalization } = pageLevelLocalization;

export default function AffiliateCard({
  price,
  termsUrl,
  productName,
  slug,
  className,
  ...props
}: Props) {
  const { profile, isAuthenticated } = useGetProfile();
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const { setShow } = useCommonModalStore();
  const [affiliateUrl, setAffiliateUrl] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      if (profile && profile?.affiliateRuleAccepted && profile?.affiliateTag) {
        updateAffiliateUrl(slug, profile?.affiliateTag);
      }
    }
  }, [profile]);

  useEffect(() => {
    if (isCopied) {
      showToast({ message: productLocalization.linkIsCopied, type: 'success' });
    }
  }, [isCopied]);

  const updateAffiliateUrl = (slug: string, affiliateTag: string) => {
    setAffiliateUrl(`${process.env.NEXT_PUBLIC_MAIN_URL}/web/${slug}/?affid=${affiliateTag}`);
  };

  const handleAffiliateButtonClick = () => {
    if (isAuthenticated) {
      if (profile && profile?.affiliateRuleAccepted) {
        return;
      } else {
        handleOpenRulesModal();
      }
    } else {
      handleAuthModal();
    }
  };

  const handleGetAffiliateClick = () => {
    copyToClipboard(affiliateUrl);
  };

  const handleAuthModal = () => {
    setShow(true, {
      DialogPanelProps: {
        className: 'rounded-xl overflow-hidden shadow-none!  p-0! bg-transparent!',
      },
      Body() {
        return <Auth />;
      },
    });
  };

  const handleOpenRulesModal = () => {
    setShow(true, {
      DialogPanelProps: {
        className: 'rounded-xl p-0! max-w-[600px]',
      },
      Body() {
        return (
          <AffiliateRulesModal
            onUpdateAffiliateUrl={(affiliateTag) => {
              updateAffiliateUrl(slug, affiliateTag);
            }}
          />
        );
      },
    });
  };

  return (
    <Section
      className={cn(
        'bg-[linear-gradient(180deg,_#1E212A_0%,#232531_100%)] p-0! pb-[3px]!',
        className,
      )}
      {...props}
    >
      <Container className='px-[26px] pt-[42px]'>
        <XImage
          src={imagesList.backgrounds.affiliateBG}
          alt=''
          className='w absolute top-0 left-0'
          width={145}
          height={124}
        />
        <Container center className='justify-between'>
          <Container className='w-[65%]'>
            <Text className='text-[#919AA7]'>{productLocalization.affiliateTitlePart1}</Text>
            <Text
              data-cy={DATA_CY_AFFILIATE_CARD_PRODUCT_TITLE}
              bold
              className='text-[#E5E8EB]'
            >
              {productLocalization.affiliateTitlePart2}
              {productName.slice(0, 30) + '...'}
              {productLocalization.affiliateTitlePart3}
            </Text>
          </Container>
          <Container className='relative h-[90px] w-[30%]'>
            <XImage
              src={imagesList.icons.affiliateLabelIcon}
              alt=''
              className='absolute z-10 object-contain'
              fill
            />
            <Container center className='absolute inset-0 z-20 flex-col'>
              <Text
                bold
                className='bg-[linear-gradient(180deg,_#FFC107_21.88%,_#FF9606_78.05%)] bg-clip-text px-0.5 text-[20px] text-transparent'
              >
                {priceFormatter(price)}
              </Text>
              <Text bold className='text-[9px] text-[#E5E8EB]'>
                {productLocalization.profitLabel}
              </Text>
            </Container>
          </Container>
        </Container>
        <Text className='pt-4 pb-5 text-center text-[13px] text-[#919AA7]'>
          {productLocalization.earnPerProduct(priceFormatter(price))}
        </Text>
      </Container>

      <Container center className='w-full justify-between gap-4 rounded-b-lg bg-[#13151D] p-6'>
        {isAuthenticated && affiliateUrl.length > 0 ? (
          <Container
            onClick={handleGetAffiliateClick}
            className='relative h-[41px] w-full max-w-[142px] rounded-md bg-[linear-gradient(1264.56deg,_#FFC107_1.61%,_#FF7B01_96.89%)] p-[1px]'
          >
            <Button className='h-full w-full rounded-md bg-gray-800 bg-[linear-gradient(180deg,_#13151D_0%,_#191C25_23.63%)] text-[#FF9606] opacity-100 transition-opacity duration-300 hover:opacity-0'>
              {productLocalization.getAffiliateLink}
            </Button>
            <Button
              data-cy={DATA_CY_AFFILIATE_CARD_GET_LINK}
              className='absolute inset-0 h-full w-full rounded-md bg-gray-800 bg-[linear-gradient(257.48deg,_#FF9606_4.12%,_#F9BB00_95.45%)] text-white opacity-0 transition-opacity duration-300 hover:opacity-100'
            >
              {productLocalization.getAffiliateLink}
            </Button>
          </Container>
        ) : (
          <Container
            onClick={handleAffiliateButtonClick}
            className='relative h-[41px] w-full max-w-[142px] rounded-md bg-[linear-gradient(1264.56deg,_#FFC107_1.61%,_#FF7B01_96.89%)] p-[1px]'
          >
            <Button className='h-full w-full rounded-md bg-gray-800 bg-[linear-gradient(180deg,_#13151D_0%,_#191C25_23.63%)] text-[#FF9606] opacity-100 transition-opacity duration-300 hover:opacity-0'>
              {productLocalization.startAffiliate}
            </Button>
            <Button
              data-cy={DATA_CY_AFFILIATE_CARD_START_AFFILIATE}
              className='absolute inset-0 h-full w-full rounded-md bg-gray-800 bg-[linear-gradient(257.48deg,_#FF9606_4.12%,_#F9BB00_95.45%)] text-white opacity-0 transition-opacity duration-300 hover:opacity-100'
            >
              {productLocalization.startAffiliate}
            </Button>
          </Container>
        )}
        <Link
          href={termsUrl}
          className='w-full max-w-[142px]'
          target='_blank'
          prefetch={false}
          data-cy={DATA_CY_AFFILIATE_CARD_MORE_INFO}
        >
          <Button className='h-[41px] w-full rounded-md bg-[#2D2F3B] text-[#C1C3C8] hover:bg-[#C1C3C8] hover:text-[#2D2F3B]'>
            {productLocalization.moreInfo}
          </Button>
        </Link>
      </Container>
    </Section>
  );
}
